import TranslationKeys from "../../../../contexts/I18nContext/translationKeys";
import {
  WhmAutocomplete,
  WhmBox,
  WhmFormHelperText,
  WhmStack,
  WhmTextField,
} from "@securitize/reactjs-whm";
import { Translate, TranslateHtml } from "../../../Translation/Translate";
import { InfoCard } from "../../shared/InfoCard";
import React, { useContext } from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { InvestorDto } from "../../../../data/dtos/investor.dto";
import { isUS, statesUS } from "../../../Registration/countriesList";
import { CountryListItem } from "../../../../data/dtos/get-registration-countries.dto";
import { InvestorTypes } from "../../../../data/types/investors";
import {
  placeholderTextFieldStyle,
  textFieldStyle,
  wrapBox,
} from "../../shared/styles";
import { I18nContext } from "../../../../contexts/I18nContext";
import { useFormikContext } from "formik";
import FormikWhmTextField from "../../../shared/FormikWhmTextField";
import { EntityKYBFormType } from "./validations";
import CountryTextField from "../../../shared/CountryTextField/CountryTextField";
import { useFlaggedFieldState } from "./entityFieldKeyMap";
import { FlaggedFieldContainer } from "./FlaggedFieldCallout";

interface AddressEditableCardProps {
  investorInfoQuery: UseQueryResult<InvestorDto, Error>;
  kybPending: boolean;
  onNotifyValueChange: (values: EntityKYBFormType) => void;
  flaggedFields: Map<string, string>;
  investorId: string;
  requestedUpdatesRoundId?: string;
}

export const AddressEditableCard: React.FC<
  React.PropsWithChildren<AddressEditableCardProps>
> = ({
  investorInfoQuery,
  kybPending,
  onNotifyValueChange,
  flaggedFields,
  investorId,
  requestedUpdatesRoundId,
}) => {
  const { getTranslation } = useContext(I18nContext);

  const isEntity =
    investorInfoQuery.data?.investorType === InvestorTypes.ENTITY;

  const { handleBlur, values, errors, setFieldValue, validateForm } =
    useFormikContext<EntityKYBFormType>();

  const streetFlag = useFlaggedFieldState(
    investorId,
    "address.street",
    values.address.street,
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const entranceFlag = useFlaggedFieldState(
    investorId,
    "address.entrance",
    values.address.entrance,
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const cityFlag = useFlaggedFieldState(
    investorId,
    "address.city",
    values.address.city,
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const zipFlag = useFlaggedFieldState(
    investorId,
    "address.zip",
    values.address.zip,
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const countryCodeFlag = useFlaggedFieldState(
    investorId,
    "address.countryCode",
    values.address.countryCode ?? "",
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const stateFlag = useFlaggedFieldState(
    investorId,
    "address.state",
    values.address.state ?? "",
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const hasFlaggedFields =
    streetFlag.isFlagged ||
    entranceFlag.isFlagged ||
    cityFlag.isFlagged ||
    zipFlag.isFlagged ||
    countryCodeFlag.isFlagged ||
    stateFlag.isFlagged;

  return (
    <InfoCard
      title={
        <Translate
          translationKey={
            isEntity
              ? TranslationKeys.PROFILE_ENTITY_INFORMATION_ADDRESS_CARD_TITLE
              : TranslationKeys.PROFILE_PERSONAL_INFORMATION_ADDRESS_CARD_TITLE
          }
        />
      }
      subtitle={
        <Translate
          translationKey={
            isEntity
              ? TranslationKeys.PROFILE_ENTITY_INFORMATION_ADDRESS_CARD_SUBTITLE
              : TranslationKeys.PROFILE_PERSONAL_INFORMATION_ADDRESS_CARD_SUBTITLE
          }
        />
      }
      footer={
        !kybPending && (
          <TranslateHtml
            translationKey={
              isEntity
                ? TranslationKeys.PROFILE_ENTITY_INFORMATION_ADDRESS_CARD_FOOTER
                : TranslationKeys.PROFILE_PERSONAL_INFORMATION_ADDRESS_CARD_FOOTER
            }
          />
        )
      }
    >
      <WhmStack sx={{ marginTop: "32px" }}>
        {(errors.address || hasFlaggedFields) && (
          <WhmFormHelperText error sx={{ marginBottom: "16px !important" }}>
            <Translate
              translationKey={
                TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_GENERIC_SECTION_ERROR
              }
            />
          </WhmFormHelperText>
        )}
        <WhmBox
          sx={{
            ...wrapBox,
            gap: "16px",
          }}
        >
          <FlaggedFieldContainer
            sx={textFieldStyle}
            comment={streetFlag.isFlagged ? streetFlag.comment : undefined}
          >
            <FormikWhmTextField
              name="address.street"
              dataTestId={"profile-information-address-street-field"}
              label={
                <Translate
                  translationKey={
                    TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_ADDRESS
                  }
                />
              }
              placeholder={getTranslation(
                TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_STREET_PLACEHOLDER,
              )}
              onBlur={(event) => {
                void setFieldValue("address.street", event.target.value.trim());
                onNotifyValueChange(values);
                handleBlur(event);
              }}
              disabled={!kybPending && !streetFlag.isFlagged}
              error={!!errors.address?.street || streetFlag.isFlagged}
            />
          </FlaggedFieldContainer>
          <FlaggedFieldContainer
            sx={textFieldStyle}
            comment={entranceFlag.isFlagged ? entranceFlag.comment : undefined}
          >
            <FormikWhmTextField
              name="address.entrance"
              dataTestId={"profile-information-address-additional-info-field"}
              label={
                <Translate
                  translationKey={
                    TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_ADDITIONAL_INFO
                  }
                />
              }
              placeholder={getTranslation(
                TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_ADDITIONAL_INFO_PLACEHOLDER,
              )}
              onBlur={(event) => {
                onNotifyValueChange(values);
                handleBlur(event);
              }}
              disabled={!kybPending && !entranceFlag.isFlagged}
              error={!!errors.address?.entrance || entranceFlag.isFlagged}
            />
          </FlaggedFieldContainer>
          <FlaggedFieldContainer
            sx={textFieldStyle}
            comment={cityFlag.isFlagged ? cityFlag.comment : undefined}
          >
            <FormikWhmTextField
              name="address.city"
              dataTestId={"profile-information-address-city-field"}
              label={
                <Translate
                  translationKey={
                    TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_CITY
                  }
                />
              }
              placeholder={getTranslation(
                TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_CITY_PLACEHOLDER,
              )}
              onBlur={(event) => {
                void setFieldValue("address.city", event.target.value.trim());
                onNotifyValueChange(values);
                handleBlur(event);
              }}
              disabled={!kybPending && !cityFlag.isFlagged}
              error={!!errors.address?.city || cityFlag.isFlagged}
            />
          </FlaggedFieldContainer>
        </WhmBox>

        <WhmBox
          sx={{
            ...wrapBox,
            gap: "16px",
            marginTop: "16px",
          }}
        >
          <FlaggedFieldContainer
            sx={textFieldStyle}
            comment={zipFlag.isFlagged ? zipFlag.comment : undefined}
          >
            <FormikWhmTextField
              name="address.zip"
              dataTestId="profile-information-address-zip-field"
              label={
                <Translate
                  translationKey={
                    TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_POSTAL_CODE
                  }
                />
              }
              placeholder={getTranslation(
                TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_ZIP_PLACEHOLDER,
              )}
              onBlur={(event) => {
                void setFieldValue("address.zip", event.target.value.trim());
                onNotifyValueChange(values);
                handleBlur(event);
              }}
              disabled={!kybPending && !zipFlag.isFlagged}
              error={!!errors.address?.zip || zipFlag.isFlagged}
            />
          </FlaggedFieldContainer>

          <FlaggedFieldContainer
            sx={textFieldStyle}
            comment={
              countryCodeFlag.isFlagged ? countryCodeFlag.comment : undefined
            }
          >
            <CountryTextField
              name="address.countryCode"
              disabled={!kybPending && !countryCodeFlag.isFlagged}
              fieldError={
                !!errors.address?.countryCode || countryCodeFlag.isFlagged
              }
              onBlur={handleBlur}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onChange={async (_, countryItem: CountryListItem | null) => {
                const countryCode = countryItem?.value ?? null;
                const valuesToValidate = {
                  ...values,
                  address: {
                    ...values.address,
                    countryCode: countryItem?.value ?? undefined,
                    state: isUS(countryCode || "")
                      ? values.address.state
                      : undefined,
                  },
                };

                await setFieldValue("address.countryCode", countryCode);
                if (!isUS(countryCode || "")) {
                  await setFieldValue("address.state", undefined);
                }

                await validateForm(valuesToValidate);
                onNotifyValueChange(valuesToValidate);
              }}
              value={values.address.countryCode ?? ""}
              dataTestId="profile-information-address-country-field"
              label={
                <Translate
                  translationKey={
                    TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_COUNTRY
                  }
                />
              }
              placeholder={getTranslation(
                TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_COUNTRY_PLACEHOLDER,
              )}
            />
          </FlaggedFieldContainer>

          {values.address.countryCode === "US" ? (
            <FlaggedFieldContainer
              sx={textFieldStyle}
              comment={stateFlag.isFlagged ? stateFlag.comment : undefined}
            >
              <WhmAutocomplete
                options={statesUS}
                value={
                  statesUS.find(
                    (state) => state.value === values.address.state,
                  ) || null
                }
                fullWidth
                disabled={!kybPending && !stateFlag.isFlagged}
                onChange={(_, newValue) => {
                  void setFieldValue(
                    "address.state",
                    newValue ? newValue.value : undefined,
                  );
                  onNotifyValueChange({
                    ...values,
                    address: {
                      ...values.address,
                      state: newValue?.value,
                    },
                  });
                }}
                onBlur={handleBlur}
                clearText={getTranslation(TranslationKeys.AUTOCOMPLETE_CLEAR)}
                openText={getTranslation(TranslationKeys.AUTOCOMPLETE_OPEN)}
                closeText={getTranslation(TranslationKeys.AUTOCOMPLETE_CLOSE)}
                renderInput={(props) => (
                  // We don't want a FormikWhmTextField here because we don't want to connect it with the values.
                  // That will be handled by the auto complete input.
                  <WhmTextField
                    {...props}
                    error={!!errors.address?.state || stateFlag.isFlagged}
                    placeholder={getTranslation(
                      TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_STATE_PLACEHOLDER,
                    )}
                    inputProps={{
                      // eslint-disable-next-line react/prop-types
                      ...props.inputProps,
                      "data-test-id": "profile-information-address-state-field",
                    }}
                    label={
                      <Translate
                        translationKey={
                          TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_STATE
                        }
                      />
                    }
                  />
                )}
              />
            </FlaggedFieldContainer>
          ) : (
            <WhmBox sx={placeholderTextFieldStyle} />
          )}
        </WhmBox>
      </WhmStack>
    </InfoCard>
  );
};
